import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { sessionChannel, broadcastSession, onSessionStorage, setLogoutReason } from "@/lib/session";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // onClick={logout} passe un event : on ne garde reason que si c'est une string.
  const logout = async (reason) => {
    const r = typeof reason === "string" ? reason : null;
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    setUser(null);
    setLogoutReason(r);
    broadcastSession({ type: "logout", reason: r });
    window.location.href = "/";
  };

  // Déconnexion multi-onglets : si un onglet se déconnecte, les autres suivent.
  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev.data;
      if (d && d.type === "logout") {
        setUser(null);
        setLogoutReason(d.reason);
        if (window.location.pathname !== "/") window.location.href = "/";
      }
    };
    const ch = sessionChannel();
    if (ch) ch.addEventListener("message", onMsg);
    const offStorage = onSessionStorage(onMsg);
    return () => { if (ch) ch.removeEventListener("message", onMsg); offStorage(); };
  }, []);

  const can = useCallback(
    (feature) => user?.role === "admin" || !!user?.permissions?.[feature],
    [user]
  );

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
